import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from '@/shared/layouts/AppShell'
import { AuthGuard } from '@/features/auth/AuthGuard'
import { CallbackPage } from '@/core/auth/CallbackPage'
import { CatalogPage } from '@/features/catalog/CatalogPage'
import { ConnectorDetailPage } from '@/features/catalog/ConnectorDetail'
import { ConnectionsPage } from '@/features/connections/ConnectionsPage'
import { ConnectPage } from '@/features/connections/ConnectPage'
import { ConnectionDetailPage } from '@/features/connections/ConnectionDetailPage'
import { ActionRunnerPage } from '@/features/actions/ActionRunnerPage'
import { ExecutionsPage } from '@/features/executions/ExecutionsPage'
import { ExecutionDetailPage } from '@/features/executions/ExecutionDetail'
import { GraphsPage } from '@/features/brain/graphs/GraphsPage'
import { BrainPage } from '@/features/brain/BrainPage'
import { ExtractorsPage } from '@/features/brain/agents/AgentsPage'
import { AgentsPage } from '@/features/agents/AgentsPage'
import { AgentChatPage } from '@/features/agents/AgentChatPage'
import { ObjectsPage } from '@/features/brain/objects/ObjectsPage'
import { NodeDetailPage } from '@/features/brain/nodes/NodeDetailPage'
import { RecordDetailPage } from '@/features/brain/records/RecordDetailPage'
import { ExplorerPage } from '@/features/brain/explorer/ExplorerPage'
import { ChatPage } from '@/features/chat/ChatPage'
import { WorkflowsPage } from '@/features/workflows/WorkflowsPage'
import { WorkflowEditorPage } from '@/features/workflows/WorkflowEditorPage'

export const router = createBrowserRouter([
  {
    // Outside the guard on purpose. This route runs inside the OAuth popup and
    // must not be redirected to a sign-in screen — doing so breaks every OAuth
    // connect at the final step.
    path: '/auth/callback',
    element: <CallbackPage />,
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/catalog" replace /> },
      { path: 'catalog', element: <CatalogPage /> },
      { path: 'catalog/:connectorId', element: <ConnectorDetailPage /> },
      { path: 'connections', element: <ConnectionsPage /> },
      // Connecting is a page rather than a dialog: it carries the connector's
      // documentation beside the form, and it is somewhere you can be sent to.
      { path: 'connections/new/:connectorId', element: <ConnectPage /> },
      { path: 'connections/:connectionId', element: <ConnectionDetailPage /> },
      { path: 'run', element: <ActionRunnerPage /> },
      { path: 'executions', element: <ExecutionsPage /> },
      { path: 'executions/:executionId', element: <ExecutionDetailPage /> },
      // The Brain. `/brain` lists the graphs that exist; everything below it
      // names a graph in the path, so a tab, a node type, a record and a table
      // are all linkable.
      { path: 'brain', element: <GraphsPage /> },
      { path: 'brain/:graphId', element: <BrainPage /> },
      { path: 'brain/:graphId/nodes/:nodeTypeId', element: <NodeDetailPage /> },
      { path: 'brain/:graphId/records/:entityId', element: <RecordDetailPage /> },
      // The Explorer is per-graph and deliberately top-level in the shell: it
      // is where someone goes to ask a question the fixed screens do not answer.
      { path: 'brain/:graphId/explorer', element: <ExplorerPage /> },
      // Agents: a model, the graphs it may read, and the conversations it has
      // had. `/agents` lists them; `/agents/:id` is one of them, open.
      { path: 'agents', element: <AgentsPage /> },
      { path: 'agents/:agentId', element: <AgentChatPage /> },
      // The *extractors* — what turns source rows into candidate records. A
      // different thing entirely from the agents above, so a different word and
      // a different path; the workspace's Agents tab is the same screen.
      { path: 'extractors', element: <ExtractorsPage /> },
      { path: 'explorer', element: <ExplorerPage /> },
      // Not per-graph: a model belongs to the deployment, and the page exists
      // to compare one against another.
      { path: 'chat', element: <ChatPage /> },
      { path: 'explorer/:graphId', element: <ExplorerPage /> },
      // Workflows: automations over the connections. The index lists them;
      // the editor is the canvas for one.
      { path: 'workflows', element: <WorkflowsPage /> },
      { path: 'workflows/:workflowId', element: <WorkflowEditorPage /> },
      { path: 'objects', element: <ObjectsPage /> },
      { path: 'objects/:graphId', element: <ObjectsPage /> },
      { path: '*', element: <Navigate to="/catalog" replace /> },
    ],
  },
])
